/* Caricare dati dal server, con gli stati che servono e non uno di meno.
 *
 * Tre stati e non due, per la stessa ragione della sessione: "sto caricando"
 * e "non c'e' niente" sono cose diverse, e confonderle fa lampeggiare "nessuna
 * ora imputata" a chi ne ha imputate cinquanta, per il tempo di una risposta.
 *
 * **Un errore non diventa mai un elenco vuoto.** Se la richiesta fallisce, i
 * dati restano `undefined` e chi mostra la pagina fa vedere l'errore: una
 * schermata vuota dopo un guasto e' la bugia piu' facile da raccontare senza
 * accorgersene.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ErroreApi } from "./api";

export interface Caricamento<T> {
  dati: T | undefined;
  errore: string | null;
  inCorso: boolean;
  ricarica: () => Promise<void>;
}

export function useCarica<T>(
  prendi: () => Promise<T>,
  dipendenze: unknown[] = [],
): Caricamento<T> {
  const [dati, setDati] = useState<T | undefined>(undefined);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(true);
  const corrente = useRef(0);
  const montato = useRef(false);
  const leggi = useRef(prendi);
  leggi.current = prendi;

  // `prendi` cambia identita' a ogni render, quindi non puo' stare fra le
  // dipendenze: si userebbe da solo all'infinito. Le dipendenze vere le
  // dichiara chi chiama, ed e' anche piu' onesto, perche' dice cosa fa
  // ricaricare davvero.
  const esegui = useCallback(async () => {
    if (!montato.current) return;
    const richiesta = ++corrente.current;
    const valida = () => montato.current && richiesta === corrente.current;
    setInCorso(true);
    setErrore(null);
    try {
      const nuovi = await leggi.current();
      if (valida()) setDati(nuovi);
    } catch (e) {
      if (valida()) {
        setDati(undefined);
        setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a leggere i dati.");
      }
    } finally {
      if (valida()) setInCorso(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dipendenze);

  useEffect(() => {
    montato.current = true;
    setDati(undefined);
    void esegui();
    return () => {
      montato.current = false;
      ++corrente.current;
    };
  }, [esegui]);

  return { dati, errore, inCorso, ricarica: esegui };
}
